import { SuggestionsStrip } from "@/components/suggestions/SuggestionsStrip";
import { getSuggestionEventsSink, type RenderableSuggestion } from "@/domain/suggestions";
import { useUpsellSuggestions } from "@/hooks/use-suggestions";
import { useOptimisticCart } from "@/hooks/use-optimistic-cart";
import { buildSuggestionCartItem } from "@/lib/add-suggestion-to-cart";
import { getMerchantId } from "@/lib/company-storage";
import { suggestionMessage } from "@/lib/i18n/suggestions-messages";
import { markSuggestionNavigation } from "@/lib/suggestion-navigation";
import type { CartItem } from "@/types";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

/**
 * "Quem pediu isso também levou" below the bag's line items. Anchors are
 * every distinct item already in the cart; excludes stay empty since the
 * join already drops anything already in the bag.
 */
export function CartSuggestionsStrip({ cartItems }: { cartItems: CartItem[] }) {
  const merchantId = getMerchantId();
  const { addItem } = useOptimisticCart();
  const navigate = useNavigate();

  const anchorItemIds = useMemo(
    () => [...new Set(cartItems.map((line) => line.itemOffer.itemId))],
    [cartItems],
  );
  const cartItemIds = useMemo(() => new Set(anchorItemIds), [anchorItemIds]);

  const { suggestions, combo, enabled } = useUpsellSuggestions("CART", anchorItemIds, [], cartItemIds);

  if (!enabled || anchorItemIds.length === 0) return null;

  function handleAdd(suggestion: RenderableSuggestion) {
    addItem(buildSuggestionCartItem(suggestion, "CART"));
    if (merchantId) {
      getSuggestionEventsSink(merchantId).enqueue({
        type: "ADD",
        surface: "CART",
        itemIds: [suggestion.merchantItemId],
        arm: suggestion.arm,
        anchorItemId: suggestion.anchorItemId,
        items: [{ itemId: suggestion.merchantItemId, arm: suggestion.arm, anchorItemId: suggestion.anchorItemId }],
      });
    }
  }

  function openDetail(itemOfferId: string) {
    navigate(`/?item=${encodeURIComponent(itemOfferId)}`);
  }

  return (
    <SuggestionsStrip
      merchantId={merchantId}
      surface="CART"
      title={suggestionMessage("cartStripTitle")}
      suggestions={suggestions}
      combo={combo}
      onAdd={handleAdd}
      onOpenDetail={(suggestion) => {
        markSuggestionNavigation(suggestion.itemOfferId, "CART", suggestion.arm, suggestion.anchorItemId);
        openDetail(suggestion.itemOfferId);
      }}
      onOpenCombo={openDetail}
    />
  );
}
